import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, Bot, Info, Link2, Share2, User, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { NotificationProvidersTab } from '../components/Settings/NotificationProvidersTab';
import { FacebookBrandIcon, InstagramBrandIcon } from '../components/social/SocialBrandIcons';
import { API_ENDPOINTS } from '../constants';
import { departmentService } from '../services/departmentService';
import { socialTestService, type MetaConnectedAppRow } from '../services/socialTestService';
import { Tabs } from '../components/ui/Tabs';
import { TextArea } from '../components/ui/Field';
import { Surface } from '../components/ui/Surface';

/**
 * Settings page component
 *
 * @returns {JSX.Element} Settings page component
 */
export const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'account' | 'users' | 'departments' | 'notifications' | 'ai-platforms' | 'social'>('account');
  const [metaOAuthLoading, setMetaOAuthLoading] = useState(false);
  const [metaConnected, setMetaConnected] = useState<MetaConnectedAppRow[]>([]);
  const [metaConnectedLoading, setMetaConnectedLoading] = useState(false);
  const [departments, setDepartments] = useState<any[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentSaving, setDepartmentSaving] = useState(false);
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [departmentDescription, setDepartmentDescription] = useState('');
  const [editingDepartmentId, setEditingDepartmentId] = useState<string | null>(null);

  const tabConfig = useMemo(
    () => [
      { id: 'account' as const, label: 'Account Information', icon: User },
      { id: 'users' as const, label: 'Users & Teams', icon: Users },
      { id: 'departments' as const, label: 'Departments', icon: Users },
      { id: 'notifications' as const, label: 'Notifications', icon: Bell },
      { id: 'ai-platforms' as const, label: 'AI Platforms', icon: Bot },
      { id: 'social' as const, label: 'Social', icon: Share2 },
    ],
    []
  );

  const activeTabData = tabConfig.find((tab) => tab.id === activeTab)!;

  const tabDescriptionMap: Record<typeof activeTab, string> = {
    account:
      'Set up and manage your company information on this page, including your business name, address, and other contact details.',
    users:
      'Manage internal users, teams, and workspace access controls for your organization.',
    departments:
      'Create and manage departments in your workspace, including updates and removals.',
    notifications:
      'Configure the email provider used to send appointment confirmations and other notifications.',
    'ai-platforms':
      'Manage connected AI providers, model access, and platform-level AI configuration.',
    social:
      'Connect Facebook Pages and linked Instagram Business accounts. Configure Meta webhooks for comment events.',
  };

  const loadMetaConnected = useCallback(async () => {
    setMetaConnectedLoading(true);
    try {
      const res = await socialTestService.listMetaConnectedApps();
      setMetaConnected(res.connectedApps ?? []);
    } catch {
      toast.error('Could not load connected Meta apps.');
      setMetaConnected([]);
    } finally {
      setMetaConnectedLoading(false);
    }
  }, []);

  useEffect(() => {
    const status = searchParams.get('social_oauth');
    if (!status) return;
    setActiveTab('social');
    const detail = searchParams.get('detail');
    if (status === 'meta_ok') {
      const pages = searchParams.get('pages');
      const subscribed = searchParams.get('subscribed');
      toast.success(`Meta linked: ${pages ?? '?'} page(s), webhook subscribed for ${subscribed ?? '?'}.`);
      void loadMetaConnected();
    } else if (status === 'meta_error' || status === 'linkedin_error') {
      toast.error(`OAuth did not complete${detail ? `: ${detail}` : ''}.`);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('social_oauth');
    next.delete('detail');
    next.delete('pages');
    next.delete('subscribed');
    next.delete('linkedin_sub');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, loadMetaConnected]);

  useEffect(() => {
    if (activeTab !== 'social') return;
    void loadMetaConnected();
  }, [activeTab, loadMetaConnected]);

  const startMetaOAuth = async () => {
    setMetaOAuthLoading(true);
    try {
      const { url } = await socialTestService.getMetaAuthorizeUrl();
      window.location.assign(url);
    } catch {
      toast.error('Could not start Meta login. Ensure SOCIAL_META_* env vars are set on core-ms.');
      setMetaOAuthLoading(false);
    }
  };

  const publicApiBase = (
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_API_BASE_URL ??
    ''
  ).replace(/\/+$/, '');
  const metaWebhookUrl = publicApiBase
    ? `${publicApiBase}${API_ENDPOINTS.SOCIAL.WEBHOOK_META_PATH}`
    : '';

  const loadDepartments = useCallback(async () => {
    setDepartmentsLoading(true);
    try {
      const response: any = await departmentService.list({
        search: departmentSearch || undefined,
      });
      const items = response?.departments || response?.data?.departments || response?.data || [];
      setDepartments(Array.isArray(items) ? items : []);
    } catch (error) {
      toast.error('Failed to load departments');
    } finally {
      setDepartmentsLoading(false);
    }
  }, [departmentSearch]);

  useEffect(() => {
    if (activeTab !== 'departments') return;
    const timer = setTimeout(() => {
      loadDepartments();
    }, 250);
    return () => clearTimeout(timer);
  }, [activeTab, loadDepartments]);

  const resetDepartmentForm = () => {
    setEditingDepartmentId(null);
    setDepartmentName('');
    setDepartmentDescription('');
  };

  const handleSaveDepartment = async () => {
    if (!departmentName.trim()) {
      toast.error('Department name is required');
      return;
    }

    setDepartmentSaving(true);
    try {
      const payload = {
        name: departmentName.trim(),
        description: departmentDescription.trim() || undefined,
      };
      if (editingDepartmentId) {
        await departmentService.update(editingDepartmentId, payload);
        toast.success('Department updated');
      } else {
        await departmentService.create(payload);
        toast.success('Department created');
      }
      resetDepartmentForm();
      await loadDepartments();
    } catch (error) {
      toast.error('Failed to save department');
    } finally {
      setDepartmentSaving(false);
    }
  };

  const handleEditDepartment = (department: any) => {
    setEditingDepartmentId(department.id);
    setDepartmentName(department.name || '');
    setDepartmentDescription(department.description || '');
  };

  const handleDeleteDepartment = async (id: string) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await departmentService.remove(id);
      toast.success('Department deleted');
      if (editingDepartmentId === id) resetDepartmentForm();
      await loadDepartments();
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  const renderTabContent = () => {
    if (activeTab === 'account') {
      return (
        <Card className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input label="Business Name" placeholder="Your Company Inc." />
            <Input label="Business Email" placeholder="contact@yourcompany.com" />
            <Input label="Phone Number" placeholder="+1 (000) 000-0000" />
            <Input label="Website" placeholder="https://yourcompany.com" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Address</label>
            <TextArea
              className="w-full rounded-xl border border-agentic-gray-300 bg-white px-4 py-3 text-agentic-text-primary placeholder:text-agentic-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2"
              rows={4}
              placeholder="Enter your business address"
            />
          </div>

          <div className="flex justify-end">
            <Button>
              Save Changes
            </Button>
          </div>
        </Card>
      );
    }

    if (activeTab === 'departments') {
      return (
        <Card className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Department Name"
              placeholder="e.g., Human Resources"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
            <Input
              label="Description"
              placeholder="Optional description"
              value={departmentDescription}
              onChange={(e) => setDepartmentDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            {editingDepartmentId ? (
              <Button
                variant="ghost"
                onClick={resetDepartmentForm}
                disabled={departmentSaving}
              >
                Cancel Edit
              </Button>
            ) : null}
            <Button onClick={handleSaveDepartment} disabled={departmentSaving} loading={departmentSaving}>
              {departmentSaving ? 'Saving...' : editingDepartmentId ? 'Update Department' : 'Create Department'}
            </Button>
          </div>

          <div className="border-t border-agentic-gray-200 pt-5">
            <div className="mb-4">
              <Input
                label="Search Departments"
                placeholder="Search by name..."
                value={departmentSearch}
                onChange={(e) => setDepartmentSearch(e.target.value)}
              />
            </div>

            {departmentsLoading ? (
              <div className="py-4 text-agentic-text-secondary">Loading departments...</div>
            ) : departments.length === 0 ? (
              <div className="py-4 text-agentic-text-secondary">No departments found.</div>
            ) : (
              <div className="space-y-3">
                {departments.map((department) => (
                  <div
                    key={department.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-agentic-gray-200 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-agentic-text-primary">{department.name}</div>
                      <div className="truncate text-sm text-agentic-text-secondary">{department.description || 'No description'}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditDepartment(department)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDeleteDepartment(department.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      );
    }

    if (activeTab === 'social') {
      return (
        <Card className="space-y-6">
          <p className="text-sm text-agentic-text-secondary">
            Meta uses Facebook Login for your Pages. Register the Meta app redirect URL on the Meta developer portal so it matches
            your API gateway (same host as <code className="text-xs">REACT_APP_API_URL</code>), e.g.{' '}
            <code className="break-all text-xs">…/core/social/oauth/callback/meta</code>.
          </p>
          <div>
            <Button onClick={startMetaOAuth} disabled={metaOAuthLoading} loading={metaOAuthLoading}>
              Connect Facebook / Instagram (Meta)
            </Button>
          </div>
          <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 px-4 py-3 text-sm text-agentic-text-secondary">
            <div className="font-medium text-agentic-text-primary">Meta webhook (comment events)</div>
            <p className="mt-1">
              Subscribe in Meta for <code className="text-xs">feed</code> and related fields. Verification uses{' '}
              <code className="text-xs">SOCIAL_WEBHOOK_VERIFY_TOKEN</code> on core-ms.
            </p>
            {metaWebhookUrl ? (
              <code className="mt-2 block break-all text-xs text-agentic-text-primary">{metaWebhookUrl}</code>
            ) : (
              <p className="mt-2 text-xs text-amber-700">
                Set REACT_APP_API_URL (or REACT_APP_API_BASE_URL) so this URL can be shown.
              </p>
            )}
          </div>

          <div className="border-t border-agentic-gray-200 pt-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="font-medium text-agentic-text-primary">Connected Meta apps</div>
              <Button variant="ghost" size="sm" onClick={() => void loadMetaConnected()} disabled={metaConnectedLoading}>
                Refresh
              </Button>
            </div>
            {metaConnectedLoading ? (
              <div className="text-sm text-agentic-text-secondary">Loading…</div>
            ) : metaConnected.length === 0 ? (
              <p className="text-sm text-agentic-text-secondary">
                No Facebook pages linked yet. Connect above to link pages and any linked Instagram Business accounts.
              </p>
            ) : (
              <ul className="space-y-4">
                {metaConnected.map((row) => {
                  const hasIg = Boolean(row.instagramBusinessAccountId || row.instagramUsername);
                  return (
                    <li key={row.id} className="overflow-hidden rounded-xl border border-agentic-gray-200 bg-white shadow-sm">
                      <div className="flex flex-col lg:flex-row lg:items-stretch">
                        <div className="flex min-w-0 flex-1 gap-3 border-b border-agentic-gray-100 bg-[#1877F2]/[0.06] p-4 lg:border-b-0 lg:border-r">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1877F2] text-white shadow-sm ring-2 ring-white">
                            <FacebookBrandIcon className="h-7 w-7" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1877F2]">Facebook Page</p>
                            <p className="truncate font-semibold text-agentic-text-primary">{row.pageName || `Page ${row.pageId}`}</p>
                            <p className="mt-1 font-mono text-[11px] text-agentic-text-secondary">page_id {row.pageId}</p>
                            <p className="font-mono text-[11px] text-agentic-text-secondary">Meta user {row.metaUserId}</p>
                          </div>
                        </div>
                        {hasIg ? (
                          <div className="hidden shrink-0 flex-col items-center justify-center gap-1 border-b border-agentic-gray-100 bg-agentic-gray-50/90 px-3 py-2 lg:flex lg:border-b-0 lg:border-r lg:py-0">
                            <Link2 className="h-4 w-4 text-blue-500" aria-hidden />
                            <span className="max-w-[4.5rem] text-center text-[10px] font-semibold uppercase leading-tight tracking-wide text-agentic-text-secondary">IG linked</span>
                          </div>
                        ) : null}
                        <div className={`flex min-w-0 flex-1 gap-3 p-4 ${hasIg ? 'bg-gradient-to-br from-fuchsia-50 via-orange-50/80 to-amber-50/90' : 'border-t border-dashed border-agentic-gray-200 bg-agentic-gray-50/50 lg:border-t-0 lg:border-l lg:border-solid'}`}>
                          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm ring-2 ring-white ${hasIg ? 'bg-white' : 'bg-agentic-gray-200/80 grayscale'}`}>
                            <InstagramBrandIcon className={`h-7 w-7 ${hasIg ? '' : 'opacity-60'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-[11px] font-semibold uppercase tracking-wide ${hasIg ? 'bg-gradient-to-r from-[#bc1888] to-[#f09433] bg-clip-text text-transparent' : 'text-agentic-text-muted'}`}>Instagram Business</p>
                            {hasIg ? (
                              <>
                                <p className="truncate font-semibold text-agentic-text-primary">{row.instagramUsername ? `@${row.instagramUsername}` : 'Connected'}</p>
                                {row.instagramBusinessAccountId ? (
                                  <p className="mt-1 font-mono text-[11px] text-agentic-text-secondary">ig_business_account_id {row.instagramBusinessAccountId}</p>
                                ) : null}
                              </>
                            ) : (
                              <p className="mt-0.5 text-sm text-agentic-text-secondary">No Instagram Business account linked to this Page in Meta.</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-agentic-gray-100 bg-agentic-gray-50 px-4 py-2.5 text-xs text-agentic-text-secondary">
                        <span>
                          Webhook:{' '}
                          <span className={row.webhookSubscribed ? 'font-medium text-emerald-700' : 'font-medium text-amber-800'}>
                            {row.webhookSubscribed ? 'subscribed' : 'not subscribed'}
                          </span>
                        </span>
                        {row.lastSubscribeError ? <span className="text-amber-800">{row.lastSubscribeError}</span> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      );
    }

    if (activeTab === 'notifications') {
      return <NotificationProvidersTab />;
    }

    return (
      <Card>
        <p className="text-agentic-text-secondary">Configuration options for this section are coming soon.</p>
      </Card>
    );
  };

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="Settings"
        subtitle="A centralized hub for managing workspace configuration across account, users, channels, and AI platforms."
      />

      <Section className="py-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px,minmax(0,1fr)]">
          <Tabs variant="side" items={tabConfig} value={activeTab} onChange={(id) => setActiveTab(id as typeof activeTab)} />

          <section className="min-w-0 space-y-4">
            <div className="flex items-start gap-3">
              <Surface className="flex h-10 w-10 items-center justify-center rounded-xl border border-agentic-gray-200 bg-white text-agentic-text-primary">
                <Info className="h-5 w-5" />
              </Surface>
              <div className="min-w-0">
                <h2 className="truncate text-2xl font-semibold text-agentic-text-primary md:text-3xl">{activeTabData.label}</h2>
                <p className="mt-1 text-agentic-text-secondary">{tabDescriptionMap[activeTab]}</p>
              </div>
            </div>

            {renderTabContent()}
          </section>
        </div>
      </Section>
    </Page>
  );
};
